/**
 * CashInflowsAuditDialog
 * ──────────────────────
 * A comprehensive audit view that lists EVERY cash-IN transaction across
 * every source collection (payments, trip_payments, installment_receipt_
 * payments, flight_walkin_payments, flight_passenger_payments, manual
 * financial_entries…). Unlike the standard "Balance Details" dialog which
 * only surfaces entries mirrored into financial_entries, this dialog shows
 * the true, unfiltered cash inflow for an agency in a given period so the
 * manager can reconcile against the physical cash on hand.
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtMoney = (v) => (v == null ? '0' : Math.round(Number(v)).toLocaleString('fr-FR'));
const fmtDate = (v) => {
  if (!v) return '—';
  try { const d = new Date(v); if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return d.toLocaleDateString('fr-FR'); } catch { return '—'; }
};

const SOURCE_COLORS = {
  payments:                      'bg-emerald-100 text-emerald-800',
  operation_payments:            'bg-emerald-50  text-emerald-700',
  trip_payments:                 'bg-blue-100    text-blue-800',
  installment_receipt_payments:  'bg-amber-100   text-amber-800',
  flight_walkin_payments:        'bg-purple-100  text-purple-800',
  flight_passenger_payments:     'bg-fuchsia-100 text-fuchsia-800',
  financial_entries:             'bg-slate-100   text-slate-700',
  // 🆕 Cash-out sources
  general_expenses:              'bg-red-100     text-red-800',
  agency_expenses:               'bg-red-100     text-red-800',
  refunds:                       'bg-orange-100  text-orange-800',
  supplier_payments:             'bg-rose-100    text-rose-800',
};

export const CashInflowsAuditDialog = ({ open, onClose, agencyId, agencyName }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], totals: {} });
  // Default: last 30 days
  const today = new Date();
  const monthAgo = new Date(today); monthAgo.setDate(monthAgo.getDate() - 30);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    start_date: iso(monthAgo),
    end_date: iso(today),
    source: '', // '' = all
    search: '',
  });

  const load = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.start_date) p.set('start_date', new Date(filters.start_date).toISOString());
      if (filters.end_date)   p.set('end_date',   new Date(filters.end_date + 'T23:59:59').toISOString());
      const r = await axios.get(
        `${API}/agencies/${agencyId}/cash-inflows?${p}`,
        { headers: authHeader() },
      );
      setData({ items: r.data.items || [], totals: r.data.totals || {} });
    } catch (e) {
      toast.error('فشل التحميل: ' + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [agencyId, filters.start_date, filters.end_date]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const filteredItems = useMemo(() => {
    let list = data.items || [];
    if (filters.source) list = list.filter(x => x.source === filters.source);
    if (filters.search?.trim()) {
      const s = filters.search.trim().toLowerCase();
      list = list.filter(x =>
        (x.description || '').toLowerCase().includes(s) ||
        (x.client_name || '').toLowerCase().includes(s) ||
        (x.service_name || '').toLowerCase().includes(s) ||
        (x.employee_name || '').toLowerCase().includes(s) ||
        (x.receipt_no || '').toLowerCase().includes(s) ||
        (x.payment_no || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [data.items, filters.source, filters.search]);

  const filteredTotal = useMemo(
    () => filteredItems.reduce((s, x) => s + (Number(x.amount) || 0), 0),
    [filteredItems],
  );

  const inflowsTotal  = data.totals?.__inflows__?.sum   || 0;
  const outflowsTotal = data.totals?.__outflows__?.sum  || 0;
  const netTotal      = data.totals?.__net__?.sum       || 0;
  const inflowsCount  = data.totals?.__inflows__?.count || 0;
  const outflowsCount = data.totals?.__outflows__?.count || 0;
  const sources = Object.entries(data.totals || {}).filter(([k]) => !k.startsWith('__'));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span>📊 كل التدفقات النقدية</span>
            {agencyName && <span className="text-sm text-gray-500">— {agencyName}</span>}
          </DialogTitle>
          <p className="text-xs text-gray-600">
            رؤية شاملة لكل المبالغ الداخلة من كل المصادر: عمليات يومية، رحلات منظمة، أقساط، طيران، قيود يدوية.
          </p>
        </DialogHeader>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 bg-amber-50/50 border border-amber-200 rounded-lg p-3">
          <div>
            <Label className="text-xs">من تاريخ</Label>
            <Input type="date" value={filters.start_date}
                   onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                   data-testid="ci-start-date" />
          </div>
          <div>
            <Label className="text-xs">إلى تاريخ</Label>
            <Input type="date" value={filters.end_date}
                   onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                   data-testid="ci-end-date" />
          </div>
          <div>
            <Label className="text-xs">المصدر</Label>
            <select
              className="w-full border rounded-md px-2 py-1.5 text-sm bg-white"
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              data-testid="ci-source"
            >
              <option value="">كل المصادر</option>
              {sources.map(([k, v]) => (
                <option key={k} value={k}>{v.label} ({v.count})</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">بحث</Label>
            <Input value={filters.search}
                   onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                   placeholder="رقم وصل / عميل / وصف..."
                   data-testid="ci-search" />
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          {sources.map(([k, v]) => {
            const isOut = v.direction === 'out';
            return (
              <button
                key={k}
                onClick={() => setFilters(f => ({ ...f, source: f.source === k ? '' : k }))}
                className={`rounded-lg border p-2 text-right transition ${filters.source === k ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                data-testid={`ci-tile-${k}`}
              >
                <div className={`inline-block text-[10px] rounded px-1.5 py-0.5 font-semibold ${SOURCE_COLORS[k] || 'bg-gray-100 text-gray-800'}`}>
                  {isOut ? '⬇️ ' : '⬆️ '}{v.label}
                </div>
                <div className={`text-xl font-bold mt-1 ${isOut ? 'text-red-700' : 'text-emerald-800'}`}>
                  {fmtMoney(v.sum)} <span className="text-xs text-gray-500">د.ج</span>
                </div>
                <div className="text-[10px] text-gray-500">{v.count} سطر</div>
              </button>
            );
          })}
        </div>

        {/* Inflows / Outflows / Net summary */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3">
            <div className="text-xs text-emerald-700">⬆️ إجمالي الداخل</div>
            <div className="text-2xl font-bold text-emerald-900" data-testid="ci-inflows-total">
              {fmtMoney(inflowsTotal)} <span className="text-sm">د.ج</span>
            </div>
            <div className="text-[10px] text-emerald-600">{inflowsCount} سطر</div>
          </div>
          <div className="bg-red-50 border border-red-300 rounded-lg p-3">
            <div className="text-xs text-red-700">⬇️ إجمالي الخارج</div>
            <div className="text-2xl font-bold text-red-800" data-testid="ci-outflows-total">
              {fmtMoney(outflowsTotal)} <span className="text-sm">د.ج</span>
            </div>
            <div className="text-[10px] text-red-600">{outflowsCount} سطر</div>
          </div>
          <div className={`border-2 rounded-lg p-3 ${netTotal >= 0 ? 'bg-brand-50 border-brand-500' : 'bg-red-50 border-red-500'}`}>
            <div className="text-xs text-brand-700">💰 الرصيد الصافي</div>
            <div className={`text-3xl font-bold ${netTotal >= 0 ? 'text-brand-800' : 'text-red-800'}`} data-testid="ci-net-total">
              {fmtMoney(netTotal)} <span className="text-sm">د.ج</span>
            </div>
            <div className="text-[10px] text-gray-600">داخل − خارج</div>
          </div>
        </div>

        {(filters.source || filters.search) && (
          <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg p-2 text-center">
            <span className="text-xs text-amber-700">النتائج المفلترة: </span>
            <span className={`text-lg font-bold ${filteredTotal >= 0 ? 'text-emerald-800' : 'text-red-800'}`} data-testid="ci-filtered-total">
              {fmtMoney(filteredTotal)} د.ج
            </span>
            <span className="text-xs text-gray-500 mr-2">({filteredItems.length} سطر)</span>
          </div>
        )}

        {/* Items table */}
        <div className="border rounded-xl overflow-hidden mt-3">
          {loading ? (
            <div className="p-10 text-center text-gray-500">جارٍ التحميل…</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-10 text-center text-gray-400">
              <div className="text-4xl">📭</div>
              <p className="mt-2">لا توجد تدفقات نقدية في هذه الفترة</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm" data-testid="ci-table">
                <thead className="bg-amber-50 sticky top-0">
                  <tr>
                    <th className="p-2 text-start">التاريخ</th>
                    <th className="p-2 text-start">المصدر</th>
                    <th className="p-2 text-start">رقم الوصل / الدفعة</th>
                    <th className="p-2 text-start">اسم العميل</th>
                    <th className="p-2 text-start">🛎️ الخدمة</th>
                    <th className="p-2 text-start">الموظف</th>
                    <th className="p-2 text-start">الدفع</th>
                    <th className="p-2 text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((it, idx) => (
                    <tr key={`${it.source}-${it.id || idx}`}
                        className={`border-t hover:bg-amber-50/40 ${it.direction === 'out' ? 'bg-red-50/30' : ''}`}
                        data-testid={`ci-row-${idx}`}>
                      <td className="p-2 text-xs whitespace-nowrap">{fmtDate(it.date)}</td>
                      <td className="p-2">
                        <span className={`text-[10px] rounded px-1.5 py-0.5 font-semibold ${SOURCE_COLORS[it.source] || 'bg-gray-100 text-gray-800'}`}>
                          {it.direction === 'out' ? '⬇️ ' : '⬆️ '}{it.source_label}
                        </span>
                      </td>
                      <td className="p-2 font-mono text-xs">{it.receipt_no || it.payment_no || it.payment_number || '—'}</td>
                      <td className="p-2 text-xs">
                        {it.client_name || it.customer_name || it.supplier_name || it.vendor || '—'}
                      </td>
                      <td className="p-2 text-xs" data-testid={`ci-service-${idx}`}>
                        {it.service_name || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="p-2 text-xs">{it.employee_name || '—'}</td>
                      <td className="p-2 text-xs">{it.payment_method || it.method || '—'}</td>
                      <td className={`p-2 text-end font-bold whitespace-nowrap ${it.direction === 'out' ? 'text-red-700' : 'text-emerald-800'}`}>
                        {fmtMoney(it.amount)} د.ج
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} data-testid="ci-close">إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CashInflowsAuditDialog;
